/* eslint-disable no-console */
import { ILogger } from "./ILogger";
import { LoggingLevel, LoggingLevelType, LoggingMessage } from "./types";
import { deepArrayCopyOrEmptyIfError } from "./utils";

/**
 * Logger, který všechny zprávy zapisuje do konzole prohlížeče.
 *
 * **POZOR**: Nesubstituční argumenty loguje jako SNAPSHOT!
 *
 * @includeToDoc
 */
export class ConsoleLogger implements ILogger {
    private static _PURE_MESSAGE_TYPES = ["string", "number", "boolean"];

    public log(section: string, level: LoggingLevelType, message: LoggingMessage, ...args: any[]): void {
        const logFunction = _getLogFunction(level);
        this._log(logFunction, section, message, ...args);
    }

    /**
     * Zaloguje přes `console` funkcí `logFunction`.
     * Jako zprávu do console předá vždy řetězec obsahující prefix s oblastí.
     * Např. `this._log(console.info, "mySection", "myMessage") -> console.info("[mySection] myMessage")`.
     *
     * Kvůli tomu také řeší to, zda-li `messageOrArgument` se do console předá jako message nebo jako argument:
     * - Jako message se předává pouze, pokud má smysl k `messageOrArgument` prefixovat oblast.
     * - pokud `messageOrArgument` je primitivní (string/number/boolean), pak se předá jako message
     * - jinak se přidá jako první argument k `args`.
     *
     * @param logFunction Jedna z: `console.log/group/groupCollapsed/info/warn/debug/error`.
     * @param section Přidá se jako prefix ke zprávě. Např. `this._log(console.info, "mySection", "myMessage) -> console.info("[mySection] myMessage")`
     * @param messageOrArgument Viz popis výše.
     * @param args Parametry pro předání do `console`. Může se k nim na první pozici přidat i `messageOrArgument`. Viz výše.
     * @example message je číslo
     * this._log(console.info, "mySection", 123.456) -> console.info("[mySection] 123.456")
     * @example message je objekt
     * this._log(console.info, "mySection", {first: "Michael", age: 17}) -> console.info("[mySection]", {first: "Michael", age: 17})
     */
    private _log(logFunction: (...args: any[]) => void, section: string, messageOrArgument: LoggingMessage, ...args: any[]): void {
        const { pureMessage, pureArgs } = (ConsoleLogger._PURE_MESSAGE_TYPES.indexOf(typeof messageOrArgument) >= 0)
            ? ({ pureMessage: messageOrArgument, pureArgs: args })
            : ({ pureMessage: "", pureArgs: [messageOrArgument, ...args] });
        this._logPure(logFunction, section, pureMessage, ...pureArgs);
    }

    /**
     * Core implementace k {@link _log}. Neřeší již `messageOrArgument`.
     */
    private _logPure(logFunction: (...args: any[]) => void, section: string, message: string, ...args: any[]): void {
        const argsForConsole = deepArrayCopyOrEmptyIfError(args);
        const messageForConsole = (typeof message === "string")
            ? _escapeForConsole(message, argsForConsole.length)
            : message;
        logFunction(`[${section}] ${messageForConsole}`, ...argsForConsole);
    }

    public logException(section: string, level: LoggingLevelType, exception: Error): void {
        _getLogFunction(level)(`[${section}]`, exception);
    }

    public group(section: string, message: LoggingMessage, ...args: any[]): void {
        this._log(console.group, section, message, ...args);
    }

    public groupCollapsed(section: string, message: LoggingMessage, ...args: any[]): void {
        this._log(console.groupCollapsed, section, message, ...args);
    }

    public groupEnd(): void {
        console.groupEnd();
    }
}

const doNothing = () => { /* Prázdná arrow funkce */ };

const _getLogFunction = (level: LoggingLevelType) => {
    if (level >= LoggingLevel.debug) {
        return console.info; // schválně logujeme debug závažnost pomocí console.info, abychom měli příjemnější chování v konzoli prohlížeče a nebylo potřeba výpisy extra zapínat i v ní
    } else if (level >= LoggingLevel.info) {
        return console.info;
    } else if (level >= LoggingLevel.warning) {
        return console.warn;
    } else if (level >= LoggingLevel.error) {
        return console.error;
    } else {
        return doNothing;
    }
};

/**
 * Vrátí oescapovanou `message`, aby v ní již `console.log/warn/error/debug` nic nenahrazoval.
 */
const _escapeForConsole = (message: string, argsForConsoleCount: number): string => {
    let downCounter = argsForConsoleCount;

    const withPercentageEscaped = (argsForConsoleCount > 0)
        ? message.replace(/(%)(?![sidfoOc])/g, "%%")
        : message;
    return withPercentageEscaped.replace(/(%)(?=[sidfoOc])/g, (match) => (--downCounter >= 0) ? "%%" : match);
};
